/**
 * The job brief: "What do I need for my 8 o'clock?" / "Tell me about the job
 * at Acacia Avenue", answered as one strip inside the reply
 * (docs/intelligent-chat-answers-plan.md, "Job brief").
 *
 * What it carries, and where each part comes from:
 *
 *   · the appliance on the property's register (`property_appliances`);
 *   · getting in: the job's `access_state` (has anybody been asked, and what
 *     did they say) and the property's door pin;
 *   · Paid so far, ONLY for a seat holding `view_invoices`;
 *   · what was quoted, with quantities and NO money (`getJobQuoteSpec`);
 *   · the property's own notes, verbatim;
 *   · earlier visits at the same address, newest first, capped and counted;
 *   · Open the job.
 *
 * 🚨 "Parking" and "Key safe" have NO column. The design draws them as tiles;
 * the data cannot back them. `properties.notes` is the field the access work
 * named as the home for "access notes, parking, stopcock location", so it is
 * shown as written, under Notes, and nothing is parsed out of it. A tile
 * reading "Key safe by the door" lifted from a sentence is a fact nobody
 * recorded, said with the authority of a field.
 *
 * 🚨 The appliance says what and where, never how old. `installed_on` is filled
 * on a few per cent of rows; when it is empty the tile says nothing about age.
 *
 * Pure and directive-free (anti-pattern #18): the panel, the Mucka Bar and the
 * oversight grader all import it. The server read is `job-brief-data.ts`.
 */
import { ACCESS_STATE_COPY, isAccessState } from '@/lib/access/state';
import { applianceSubtitle, applianceTitle, isInUse, type PropertyAppliance } from '@/lib/appliances/display';
import { invoiceBalance, invoiceGross, isOwed, type InvoiceMoneyRow } from '@/lib/invoices/status';
import { ukDate } from '@/lib/utils/date-format';

/** Quote lines shown; the rest are counted, never dropped silently. */
export const JOB_BRIEF_QUOTE_LINES_MAX = 8;
/** Earlier visits shown when the list is opened; the rest are counted. */
export const JOB_BRIEF_VISITS_MAX = 5;
/** Property notes past this are cut with an ellipsis; the job page has them whole. */
export const JOB_BRIEF_NOTES_MAX = 600;

export type JobBriefTileKind = 'appliance' | 'access' | 'paid';

export interface JobBriefTile {
  kind: JobBriefTileKind;
  /** The small caps label: "Boiler", "Access", "Paid so far". */
  label: string;
  value: string;
  detail: string | null;
}

export interface JobBriefLine {
  description: string;
  /** Shown as "× 3" when it is not 1. Never a price. */
  quantity: number | null;
}

export interface JobBriefVisit {
  jobId: string;
  /** "4 Mar 2026", London, built server-side. Null for an undated job. */
  when: string | null;
  /** "Boiler Service · Dave". */
  what: string;
  path: string;
}

export interface JobBriefBlock {
  kind: 'job-brief';
  jobId: string;
  /** "Boiler Service · Dee Walsh". */
  title: string;
  /** "Sat 11 Oct, 08:00", London. Null when the job is not in the diary. */
  when: string | null;
  /** "Flat 2, 14 Acacia Avenue, BS5 9ZZ". Null when no property is attached. */
  where: string | null;
  tiles: JobBriefTile[];
  /** `properties.notes`, verbatim (cut at JOB_BRIEF_NOTES_MAX). */
  notes: string | null;
  /** What was quoted, or null when the job has no accepted quote with lines. */
  quote: {
    /** "quote" or "estimate" — the word this business printed. */
    noun: string;
    reference: string | null;
    lines: JobBriefLine[];
    more: number;
  } | null;
  visits: JobBriefVisit[];
  /** Earlier visits beyond those listed. */
  visitsMore: number;
  /**
   * True when Paid so far was left off because this seat cannot see invoices
   * (#23). Carried so the grader can tell "no money because of the role" from
   * "no money because nothing was read".
   */
  moneyWithheld: boolean;
  path: string;
}

/** The job as the reader found it, reduced to what the brief shows. */
export interface JobBriefJob {
  id: string;
  type: string | null;
  /** Display name, already resolved; empty when there is no client. */
  clientName: string;
  /** ISO timestamp, or null when unscheduled. */
  scheduledStart: string | null;
  accessState: string | null;
}

export interface JobBriefProperty {
  label: string | null;
  addressLine1: string | null;
  postcode: string | null;
  notes: string | null;
  /** The door pin, when one is held (`access_lat` set). */
  pin: { words: string | null } | null;
}

export interface JobBriefVisitRow {
  id: string;
  type: string | null;
  scheduledStart: string | null;
  engineerName: string | null;
}

export interface JobBriefMoney {
  /** Received across this job's invoices, in pounds. */
  paid: number;
  /** Still owed on its sent invoices, in pounds. */
  owed: number;
  /** Invoices that have gone out (not drafts, not cancelled). */
  invoiced: number;
}

export interface JobBriefInput {
  job: JobBriefJob;
  property: JobBriefProperty | null;
  /** The property's register, or null when it was not read (no property, or
   *  this seat may not see it). An empty array is a register with nothing on it. */
  appliances: PropertyAppliance[] | null;
  quote: { noun: string; reference: string | null; lines: JobBriefLine[] } | null;
  visits: JobBriefVisitRow[];
  /** Every earlier visit the query matched, of which `visits` is the newest few. */
  visitsTotal: number;
  /** Null when not shown: either withheld (see `moneyWithheld`) or not read. */
  money: JobBriefMoney | null;
  moneyWithheld: boolean;
}

function pounds(n: number): string {
  return `£${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Paid so far, from the invoices raised against the job.
 *
 * Drafts and cancelled invoices count for nothing: a draft has asked nobody
 * for money and a cancelled one was raised in error. A `paid` invoice counts in
 * full even when no payment detail was recorded — importers mark an invoice
 * paid with `paid_amount` 0 when the old system said so (TKT-1460), and "£0
 * paid" about a settled invoice is the wrong answer.
 */
export function jobBriefMoney(invoices: readonly InvoiceMoneyRow[]): JobBriefMoney {
  let paid = 0;
  let owed = 0;
  let invoiced = 0;
  for (const inv of invoices) {
    if (!inv.status || inv.status === 'draft' || inv.status === 'cancelled') continue;
    invoiced += 1;
    const gross = invoiceGross(inv);
    if (inv.status === 'paid') {
      paid += gross;
      continue;
    }
    paid += Math.min(Math.max(Number(inv.paid_amount) || 0, 0), gross);
    if (isOwed(inv.status)) owed += invoiceBalance(inv);
  }
  return { paid: round2(paid), owed: round2(owed), invoiced };
}

/** "Sat 11 Oct, 08:00", in London. */
function whenLabel(iso: string): string {
  return `${ukDate(iso, { weekday: 'short', day: 'numeric', month: 'short' })}, ${ukDate(iso, { hour: '2-digit', minute: '2-digit', hour12: false })}`;
}

function firstName(name: string | null): string | null {
  const first = (name || '').trim().split(/\s+/)[0];
  return first || null;
}

const BOILER = /boiler/i;

/**
 * The appliance tile, or null when the register was not read. Prefers a
 * boiler — "what do I need for my 8 o'clock" is a heating question far more
 * often than not — and otherwise takes the first thing in use. Decommissioned
 * rows are history (TKT-1391) and never stand for what is fitted now.
 */
function applianceTile(appliances: PropertyAppliance[] | null): JobBriefTile | null {
  if (appliances === null) return null;
  const inUse = appliances.filter(a => isInUse(a));
  if (!inUse.length) {
    return { kind: 'appliance', label: 'Appliance', value: 'None recorded', detail: "On this property's register" };
  }
  const main = inUse.find(a => BOILER.test(a.applianceType || '') || BOILER.test(applianceTitle(a))) ?? inUse[0];
  const isBoiler = BOILER.test(main.applianceType || '') || BOILER.test(applianceTitle(main));
  const detail = [
    applianceSubtitle(main) || null,
    // A year only when one was RECORDED. Never an age worked out from nothing.
    main.installedOn ? `fitted ${main.installedOn.slice(0, 4)}` : null,
    inUse.length > 1 ? `${inUse.length - 1} more on the register` : null,
  ].filter(Boolean).join(' · ');
  return { kind: 'appliance', label: isBoiler ? 'Boiler' : 'Appliance', value: applianceTitle(main), detail: detail || null };
}

/**
 * Getting in. The state is the office's answer to "has anybody been asked";
 * the door pin, when there is one, is worth more than the state's hint line.
 */
function accessTile(state: string | null, property: JobBriefProperty | null): JobBriefTile | null {
  if (!isAccessState(state)) return null;
  const copy = ACCESS_STATE_COPY[state];
  const pin = property?.pin;
  const detail = pin ? (pin.words ? `Door pin ///${pin.words}` : 'Door pin on the map') : copy.hint;
  return { kind: 'access', label: 'Access', value: copy.label, detail };
}

function paidTile(money: JobBriefMoney | null): JobBriefTile | null {
  if (!money) return null;
  const detail = money.invoiced === 0
    ? 'Nothing invoiced yet'
    : money.owed > 0
      ? `${pounds(money.owed)} still to pay`
      : 'Nothing outstanding';
  return { kind: 'paid', label: 'Paid so far', value: pounds(money.paid), detail };
}

/** The brief, from what the reader found. Pure: every string is built here. */
export function jobBriefBlock(input: JobBriefInput): JobBriefBlock {
  const { job, property } = input;
  const title = [job.type?.trim() || 'Job', job.clientName.trim() || null].filter(Boolean).join(' · ');
  const where = property
    ? [property.label, property.addressLine1, property.postcode].map(v => (v || '').trim()).filter(Boolean).join(', ') || null
    : null;
  const rawNotes = (property?.notes || '').trim();
  const notes = rawNotes
    ? (rawNotes.length > JOB_BRIEF_NOTES_MAX ? `${rawNotes.slice(0, JOB_BRIEF_NOTES_MAX).trimEnd()}…` : rawNotes)
    : null;
  const tiles = [applianceTile(input.appliances), accessTile(job.accessState, property), paidTile(input.money)]
    .filter((t): t is JobBriefTile => t !== null);
  const quoteLines = input.quote?.lines.filter(l => l.description.trim()) ?? [];
  const visits = input.visits.slice(0, JOB_BRIEF_VISITS_MAX).map(v => ({
    jobId: v.id,
    when: v.scheduledStart ? ukDate(v.scheduledStart, { day: 'numeric', month: 'short', year: 'numeric' }) : null,
    what: [v.type?.trim() || 'Job', firstName(v.engineerName)].filter(Boolean).join(' · '),
    path: `/jobs/${v.id}`,
  }));
  return {
    kind: 'job-brief',
    jobId: job.id,
    title,
    when: job.scheduledStart ? whenLabel(job.scheduledStart) : null,
    where,
    tiles,
    notes,
    quote: input.quote && quoteLines.length
      ? {
        noun: input.quote.noun || 'quote',
        reference: input.quote.reference,
        lines: quoteLines.slice(0, JOB_BRIEF_QUOTE_LINES_MAX),
        more: Math.max(0, quoteLines.length - JOB_BRIEF_QUOTE_LINES_MAX),
      }
      : null,
    visits,
    visitsMore: Math.max(0, Math.max(input.visitsTotal, input.visits.length) - visits.length),
    moneyWithheld: input.moneyWithheld,
    path: `/jobs/${job.id}`,
  };
}

/**
 * One line for the oversight transcript: which parts were on screen and how
 * many of each. Nothing a customer could be identified by — no name, no
 * address, no appliance, no figure.
 */
export function summariseJobBriefBlock(block: JobBriefBlock): string {
  const parts: string[] = [];
  parts.push(block.tiles.length ? block.tiles.map(t => (t.kind === 'paid' ? 'paid so far' : t.kind)).join(', ') : 'no tiles');
  if (block.moneyWithheld) parts.push('paid so far withheld (no invoice permission)');
  if (block.quote) {
    const n = block.quote.lines.length + block.quote.more;
    parts.push(`${n} ${block.quote.noun} line${n === 1 ? '' : 's'}`);
  }
  if (block.notes) parts.push('property notes');
  const visits = block.visits.length + block.visitsMore;
  parts.push(`${visits} earlier visit${visits === 1 ? '' : 's'}`);
  return `[job-brief: ${parts.join('; ')}; Open the job]`;
}

/** "× 3" beside a quote line, or null when it is one (or unknown). */
export function quantityMark(quantity: number | null): string | null {
  if (quantity == null || quantity === 1) return null;
  const n = Number.isInteger(quantity) ? String(quantity) : quantity.toLocaleString('en-GB', { maximumFractionDigits: 2 });
  return `× ${n}`;
}
